import { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { cn } from '@flowstate/utils';

const COLORS = {
  brain: 0x3b82f6,
  brainGlow: 0x60a5fa,
  edge: 0x06b6d4,
  cloud: 0x8b5cf6,
  user: 0x10b981,
  line: 0x3b82f6,
  line2: 0x8b5cf6,
  packet: 0xa5f3fc,
};

interface NodeData {
  position: THREE.Vector3;
  color: number;
  scale: number;
  speed: number;
  phase: number;
  mesh?: THREE.Mesh;
  glow?: THREE.Mesh;
}

interface ConnectionData {
  from: THREE.Vector3;
  to: THREE.Vector3;
  color: number;
}

function generateNetwork() {
  const nodes: NodeData[] = [];
  const connections: ConnectionData[] = [];

  nodes.push({ position: new THREE.Vector3(0, 0, 0), color: COLORS.brain, scale: 0.9, speed: 1, phase: 0 });

  const edgeCount = 8;
  const edgeRadius = 2.8;
  for (let i = 0; i < edgeCount; i++) {
    const angle = (i / edgeCount) * Math.PI * 2;
    const pos = new THREE.Vector3(
      Math.cos(angle) * edgeRadius,
      Math.sin(angle * 2) * 0.6,
      Math.sin(angle) * edgeRadius
    );
    nodes.push({ position: pos, color: COLORS.edge, scale: 0.22, speed: 1.5, phase: Math.random() * Math.PI * 2 });
    connections.push({ from: new THREE.Vector3(0, 0, 0), to: pos, color: COLORS.line });
  }

  const userCount = 5;
  const userRadius = 4.2;
  for (let i = 0; i < userCount; i++) {
    const angle = (i / userCount) * Math.PI * 2 + 0.4;
    const pos = new THREE.Vector3(
      Math.cos(angle) * userRadius,
      Math.cos(angle * 3) * 0.8,
      Math.sin(angle) * userRadius
    );
    nodes.push({ position: pos, color: COLORS.user, scale: 0.18, speed: 1.2, phase: Math.random() * Math.PI * 2 });
    connections.push({ from: new THREE.Vector3(0, 0, 0), to: pos, color: COLORS.line2 });
  }

  const cloudCount = 6;
  const cloudRadius = 5.8;
  for (let i = 0; i < cloudCount; i++) {
    const angle = (i / cloudCount) * Math.PI * 2 + 0.8;
    const pos = new THREE.Vector3(
      Math.cos(angle) * cloudRadius,
      Math.sin(angle * 2) * 1.0,
      Math.sin(angle) * cloudRadius
    );
    nodes.push({ position: pos, color: COLORS.cloud, scale: 0.28, speed: 0.8, phase: Math.random() * Math.PI * 2 });
    connections.push({ from: new THREE.Vector3(0, 0, 0), to: pos, color: COLORS.line2 });
  }

  for (let i = 1; i < nodes.length; i++) {
    const next = i + 1 < nodes.length ? i + 1 : 1;
    if (Math.random() > 0.5) {
      connections.push({ from: nodes[i].position, to: nodes[next].position, color: COLORS.line });
    }
  }

  return { nodes, connections };
}

function createStarfield() {
  const count = 1200;
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = 20 + Math.random() * 40;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.06,
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  return new THREE.Points(geometry, material);
}

function useScene(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const canvas = canvasEl;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x0f172a, 0.035);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 0, 10.5);
    camera.lookAt(0, 0, 0);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.25);
    // Three.js 0.182 type definitions: light instances are not assignable to scene.add due to generic event-map mismatch.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scene.add(ambientLight as any);
    const pointLight = new THREE.PointLight(0x60a5fa, 2, 20);
    pointLight.position.set(5, 5, 5);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scene.add(pointLight as any);
    const pointLight2 = new THREE.PointLight(0x8b5cf6, 1.5, 20);
    pointLight2.position.set(-5, -5, 5);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scene.add(pointLight2 as any);

    scene.add(createStarfield());

    const networkGroup = new THREE.Group();
    scene.add(networkGroup);

    const { nodes, connections } = generateNetwork();

    // Central brain
    const brainNode = nodes[0];
    const brainCore = new THREE.Mesh(
      new THREE.SphereGeometry(1, 32, 32),
      new THREE.MeshStandardMaterial({
        color: COLORS.brain,
        emissive: COLORS.brainGlow,
        emissiveIntensity: 0.6,
        roughness: 0.2,
        metalness: 0.8,
      })
    );
    brainCore.scale.setScalar(brainNode.scale);
    networkGroup.add(brainCore);

    const brainGlow = new THREE.Mesh(
      new THREE.SphereGeometry(1, 32, 32),
      new THREE.MeshBasicMaterial({
        color: COLORS.brainGlow,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    brainGlow.scale.setScalar(2.0);
    networkGroup.add(brainGlow);

    // Satellite nodes
    for (let i = 1; i < nodes.length; i++) {
      const node = nodes[i];
      const coreMaterial = new THREE.MeshStandardMaterial({
        color: node.color,
        emissive: node.color,
        emissiveIntensity: 0.35,
        roughness: 0.3,
        metalness: 0.6,
      });
      const core = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 24), coreMaterial);
      core.position.copy(node.position);
      core.scale.setScalar(node.scale);
      networkGroup.add(core);
      node.mesh = core;

      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(1, 16, 16),
        new THREE.MeshBasicMaterial({
          color: node.color,
          transparent: true,
          opacity: 0.08,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      glow.position.copy(node.position);
      glow.scale.setScalar(1.5);
      networkGroup.add(glow);
      node.glow = glow;
    }

    // Connections as one line segments object
    const lineGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(connections.length * 2 * 3);
    const colors = new Float32Array(connections.length * 2 * 3);
    const color = new THREE.Color();
    for (let i = 0; i < connections.length; i++) {
      const c = connections[i];
      color.setHex(c.color);
      for (let j = 0; j < 2; j++) {
        const p = j === 0 ? c.from : c.to;
        positions[i * 6 + j * 3] = p.x;
        positions[i * 6 + j * 3 + 1] = p.y;
        positions[i * 6 + j * 3 + 2] = p.z;
        colors[i * 6 + j * 3] = color.r;
        colors[i * 6 + j * 3 + 1] = color.g;
        colors[i * 6 + j * 3 + 2] = color.b;
      }
    }
    lineGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    lineGeometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const lineMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
    });
    const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
    networkGroup.add(lineSegments);

    // Data packets
    const packetGeometry = new THREE.SphereGeometry(1, 12, 12);
    const packetMaterial = new THREE.MeshBasicMaterial({
      color: COLORS.packet,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.9,
    });
    const packets = connections.map((c, i) => {
      const mesh = new THREE.Mesh(packetGeometry, packetMaterial);
      mesh.position.copy(c.from);
      mesh.scale.setScalar(0.08 + (i % 3) * 0.02);
      networkGroup.add(mesh);
      return { mesh, connection: c, speed: 0.3 + (i % 3) * 0.1, phase: i * 0.2 };
    });

    const mouse = { x: 0, y: 0 };
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    canvas.addEventListener("mousemove", handleMouseMove);

    let animationId = 0;
    const clock = new THREE.Clock();

    function animate() {
      animationId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const delta = clock.getDelta();

      networkGroup.rotation.y += delta * 0.04;
      networkGroup.rotation.x += (Math.sin(t * 0.2) * 0.04 - networkGroup.rotation.x) * 0.02;
      networkGroup.rotation.z += (mouse.x * 0.05 - networkGroup.rotation.z) * 0.02;
      networkGroup.rotation.x += (mouse.y * 0.05 - networkGroup.rotation.x) * 0.02;

      // Pulse brain
      brainCore.scale.setScalar(brainNode.scale + Math.sin(t * 2) * 0.04);
      brainGlow.scale.setScalar(2.0 + Math.sin(t * 1.5) * 0.1);

      // Pulse nodes
      for (let i = 1; i < nodes.length; i++) {
        const node = nodes[i];
        const pulse = Math.sin(t * node.speed + node.phase) * 0.03;
        const s = node.scale + pulse;
        node.mesh?.scale.setScalar(s);
        node.glow?.scale.setScalar(1.5 + pulse * 2);
      }

      // Move packets
      for (const packet of packets) {
        const phase = (t * packet.speed + packet.phase) % 1;
        packet.mesh.position.lerpVectors(packet.connection.from, packet.connection.to, phase);
        packet.mesh.scale.setScalar(0.08 + Math.sin(phase * Math.PI) * 0.04);
      }

      renderer.render(scene, camera);
    }

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      const width = Math.max(rect.width, 1);
      const height = Math.max(rect.height, 1);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }

    resize();
    window.addEventListener("resize", resize);
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      canvas?.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      lineGeometry.dispose();
      lineMaterial.dispose();
      packetGeometry.dispose();
      packetMaterial.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
    };
  }, [canvasRef]);
}

function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? <>{children}</> : null;
}

export function MyceliumNetworkScene({ className }: { className?: string }) {
  return (
    <div className={cn("relative w-full h-full", className)}>
      <ClientOnly>
        <NetworkCanvas />
      </ClientOnly>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
    </div>
  );
}

function NetworkCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useScene(canvasRef);

  return (
    <canvas
      ref={canvasRef}
      className="block h-full w-full"
      aria-label="Mycelium network visualization"
    />
  );
}
